import * as Cesium from 'cesium';
import * as satellite from 'satellite.js';
import { TLE_LINE_LEN } from '../../../lib/tle';
import { MU } from '../constants';
import type { BackendOrbitTrack, EphemerisPoint, MapSatellite, TleLines } from '../types';
import { getEnvVar } from '../../../../../../envUtils';

export function numericEpoch(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) return parsed / 1000;
  }
  return null;
}

export function buildBackendLlaSampledPosition(
  track: BackendOrbitTrack | undefined,
): { position: Cesium.SampledPositionProperty; periodSec: number; startEpoch: number } | null {
  if (!track?.orbitPoints?.length) return null;
  const stride = track.pointStride || 4;
  if (stride < 3 || track.orbitPoints.length < stride * 2) return null;

  const order = track.coordinateOrder?.length
    ? track.coordinateOrder.map((key) => String(key).toLowerCase())
    : ['time_offset_sec', 'longitude', 'latitude', 'altitude_m'];
  const timeIdx = order.findIndex(key => key.includes('time'));
  const lonIdx = order.findIndex(key => key === 'longitude' || key === 'lon');
  const latIdx = order.findIndex(key => key === 'latitude' || key === 'lat');
  const altIdx = order.findIndex(key => key.includes('alt'));
  if (lonIdx < 0 || latIdx < 0 || lonIdx >= stride || latIdx >= stride) return null;

  const baseEpoch = numericEpoch(track.startTime) ?? numericEpoch(track.fromUtc) ?? 0;
  const sampledPos = new Cesium.SampledPositionProperty(Cesium.ReferenceFrame.FIXED);
  sampledPos.setInterpolationOptions({
    interpolationAlgorithm: Cesium.LagrangePolynomialApproximation,
    interpolationDegree: 5,
  });
  let firstEpoch: number | null = null;
  let lastEpoch: number | null = null;
  let validCount = 0;

  for (let i = 0; i + stride <= track.orbitPoints.length; i += stride) {
    const timeValue = timeIdx >= 0 && timeIdx < stride ? Number(track.orbitPoints[i + timeIdx]) : validCount * (track.stepSizeSeconds ?? 60);
    const lon = Number(track.orbitPoints[i + lonIdx]);
    const lat = Number(track.orbitPoints[i + latIdx]);
    const alt = altIdx >= 0 && altIdx < stride ? Number(track.orbitPoints[i + altIdx]) : 0;
    if (!Number.isFinite(timeValue) || !Number.isFinite(lon) || !Number.isFinite(lat) || !Number.isFinite(alt)) continue;

    const epoch = baseEpoch > 0 ? baseEpoch + timeValue : timeValue;
    if (!Number.isFinite(epoch) || epoch <= 0) continue;
    sampledPos.addSample(
      Cesium.JulianDate.fromDate(new Date(epoch * 1000)),
      Cesium.Cartesian3.fromDegrees(lon, lat, alt),
    );
    firstEpoch = firstEpoch ?? epoch;
    lastEpoch = epoch;
    validCount += 1;
  }

  if (validCount < 2 || firstEpoch == null || lastEpoch == null) return null;
  sampledPos.forwardExtrapolationType = Cesium.ExtrapolationType.HOLD;
  sampledPos.backwardExtrapolationType = Cesium.ExtrapolationType.HOLD;

  return {
    position: sampledPos,
    periodSec: Math.max(60, lastEpoch - firstEpoch),
    startEpoch: firstEpoch,
  };
}

export function buildEphemerisSampledPosition(
  ephemeris: EphemerisPoint[],
): { position: Cesium.SampledPositionProperty; periodSec: number; startEpoch: number } | null {
  const valid = ephemeris
    .map(p => ({
      ...p,
      epoch: Number(p.epoch),
      x: Number(p.x),
      y: Number(p.y),
      z: Number(p.z),
    }))
    .filter(p => p.epoch > 0 && Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z))
    .sort((a, b) => a.epoch - b.epoch);

  if (valid.length < 2) return null;

  const maxAbsCoord = Math.max(...valid.flatMap(p => [Math.abs(p.x), Math.abs(p.y), Math.abs(p.z)]));
  const coordScale = maxAbsCoord < 1_000_000 ? 1000 : 1;
  const prop = new Cesium.SampledPositionProperty(Cesium.ReferenceFrame.FIXED);
  prop.setInterpolationOptions({
    interpolationAlgorithm: Cesium.LagrangePolynomialApproximation,
    interpolationDegree: 5,
  });

  valid.forEach((p) => {
    const jd = Cesium.JulianDate.fromDate(new Date(p.epoch * 1000));
    const jsDate = new Date(p.epoch * 1000);
    const gmst = satellite.gstime(jsDate);
    const ecfPos = satellite.eciToEcf({ x: p.x, y: p.y, z: p.z }, gmst);
    prop.addSample(jd, new Cesium.Cartesian3(ecfPos.x * coordScale, ecfPos.y * coordScale, ecfPos.z * coordScale));
  });

  prop.forwardExtrapolationType = Cesium.ExtrapolationType.HOLD;
  prop.backwardExtrapolationType = Cesium.ExtrapolationType.HOLD;

  return {
    position: prop,
    periodSec: Math.max(60, valid[valid.length - 1].epoch - valid[0].epoch),
    startEpoch: valid[0].epoch,
  };
}

export function buildTleSampledPosition(
  line1: string,
  line2: string,
  startDate: Date,
  orbitTurns = 4,
  samplesPerTurn = 120,
): { position: Cesium.SampledPositionProperty; periodSec: number } {
  const l1 = line1.padEnd(TLE_LINE_LEN, ' ').slice(0, TLE_LINE_LEN);
  const l2 = line2.padEnd(TLE_LINE_LEN, ' ').slice(0, TLE_LINE_LEN);
  const rec = satellite.twoline2satrec(l1, l2);
  const periodSec = (2 * Math.PI / rec.no) * 60;
  const T = periodSec > 0 ? periodSec : 5400;

  const sampledPos = new Cesium.SampledPositionProperty(Cesium.ReferenceFrame.FIXED);
  sampledPos.setInterpolationOptions({
    interpolationAlgorithm: Cesium.LagrangePolynomialApproximation,
    interpolationDegree: 5,
  });

  const startJD = Cesium.JulianDate.fromDate(startDate);
  const totalSamples = samplesPerTurn * orbitTurns;

  for (let i = 0; i <= totalSamples; i++) {
    const tSec = (i / samplesPerTurn) * T;
    const jd = Cesium.JulianDate.addSeconds(startJD, tSec, new Cesium.JulianDate());
    const jsDate = Cesium.JulianDate.toDate(jd);
    const pv = satellite.propagate(rec, jsDate);
    if (!pv.position || typeof pv.position === 'boolean') continue;
    const p = pv.position;
    const gmst = satellite.gstime(jsDate);
    const ecfPos = satellite.eciToEcf(p, gmst);
    sampledPos.addSample(jd, new Cesium.Cartesian3(ecfPos.x * 1000, ecfPos.y * 1000, ecfPos.z * 1000));
  }

  sampledPos.forwardExtrapolationType = Cesium.ExtrapolationType.HOLD;
  sampledPos.backwardExtrapolationType = Cesium.ExtrapolationType.HOLD;

  return { position: sampledPos, periodSec: T };
}

export function circlePolylinePositions(
  centerLat: number,
  centerLng: number,
  radiusM: number,
  N = 128,
): Cesium.Cartesian3[] {
  const pts: number[] = [];
  for (let i = 0; i <= N; i++) {
    const angle = (i / N) * 2 * Math.PI;
    const dlat = (radiusM / 111320) * Math.cos(angle);
    const dlng = (radiusM / (111320 * Math.cos(Cesium.Math.toRadians(centerLat)))) * Math.sin(angle);
    pts.push(centerLng + dlng, centerLat + dlat);
  }
  return Cesium.Cartesian3.fromDegreesArray(pts);
}

export function beamPulse(phase = 0, durationMs = 1800): number {
  const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
  return 0.5 + Math.sin((now / durationMs + phase) * Math.PI * 2) * 0.5;
}

export function animatedBeamMaterial(
  color: Cesium.Color,
  minAlpha: number,
  maxAlpha: number,
  phase = 0,
): Cesium.ColorMaterialProperty {
  return new Cesium.ColorMaterialProperty(new Cesium.CallbackProperty(() => (
    color.withAlpha(minAlpha + (maxAlpha - minAlpha) * beamPulse(phase))
  ), false));
}

export const getRuntimeEnv = (key: string): string | undefined => {
  try {
    return getEnvVar(key) || (process.env as Record<string, string | undefined>)[key];
  } catch (e) {
    return (process.env as Record<string, string | undefined>)[key];
  }
};

export function geodesicKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const geodesic = new Cesium.EllipsoidGeodesic(
    new Cesium.Cartographic(Cesium.Math.toRadians(lng1), Cesium.Math.toRadians(lat1)),
    new Cesium.Cartographic(Cesium.Math.toRadians(lng2), Cesium.Math.toRadians(lat2)),
  );
  return geodesic.surfaceDistance / 1000;
}
